/*
 * BAS Nav — desktop megamenu + mobile drawer interactivity.
 * Spec: designs/full-site-redesign.md §4
 *
 * Vanilla JS, no module deps. Defers cleanly when the nav isn't rendered.
 * Cart icon is NOT handled here — it ships with <cart-link>/<items-count> custom
 * elements from the legacy theme that own their own click handlers.
 */

(function () {
  'use strict';

  const ESC = 'Escape';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function focusableSelector() {
    return [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
  }

  // ---- Desktop megamenu ----

  function initMegamenu(nav) {
    const items = nav.querySelectorAll('[data-bas-nav-item]');
    let openItem = null;

    function close(item) {
      if (!item) return;
      const trigger = item.querySelector('[data-bas-nav-megamenu-trigger]');
      const menu = item.querySelector('.bas-nav__megamenu');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (menu) menu.hidden = true;
      if (openItem === item) openItem = null;
    }

    function open(item) {
      if (openItem && openItem !== item) close(openItem);
      const trigger = item.querySelector('[data-bas-nav-megamenu-trigger]');
      const menu = item.querySelector('.bas-nav__megamenu');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (menu) menu.hidden = false;
      openItem = item;
    }

    items.forEach((item) => {
      const trigger = item.querySelector('[data-bas-nav-megamenu-trigger]');
      if (!trigger) return;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        if (expanded) close(item);
        else open(item);
      });

      item.addEventListener('mouseenter', () => open(item));
      item.addEventListener('mouseleave', () => close(item));

      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close(item);
      });
    });

    // Esc closes any open menu and returns focus to its trigger.
    document.addEventListener('keydown', (e) => {
      if (e.key === ESC && openItem) {
        const trigger = openItem.querySelector('[data-bas-nav-megamenu-trigger]');
        close(openItem);
        if (trigger) trigger.focus();
      }
    });

    // Click outside closes
    document.addEventListener('click', (e) => {
      if (openItem && !openItem.contains(e.target)) close(openItem);
    });
  }

  // ---- Mobile drawer ----

  function initMobileDrawer() {
    const drawer = document.querySelector('[data-bas-mobile-drawer]');
    const trigger = document.querySelector('[data-bas-nav-trigger]');
    const closeBtn = drawer && drawer.querySelector('[data-bas-mobile-drawer-close]');
    const backdrop = drawer && drawer.querySelector('[data-bas-mobile-drawer-backdrop]');
    if (!drawer || !trigger) return;

    let lastFocused = null;

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      const focusables = drawer.querySelectorAll(focusableSelector());
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function onKeydown(e) {
      if (e.key === ESC) close();
      trapFocus(e);
    }

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      // Move focus into the drawer.
      const closeButton = drawer.querySelector('[data-bas-mobile-drawer-close]');
      if (closeButton) closeButton.focus();
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      drawer.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeydown);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    trigger.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
  }

  ready(function () {
    const nav = document.querySelector('[data-bas-nav]');
    if (nav) initMegamenu(nav);
    initMobileDrawer();
  });
})();
